import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import {
  getAllTables, bookTable, getReservationSettings,
  requestReservationOtp, verifyReservationOtp,
} from '../services/tableService';
import crmService from '../services/crmService';
import { locationsService } from '../services/locationsService';
import apiClient from '../services/api';
import { useRestaurant } from '../context/RestaurantContext';
import { CheckCircle, Loader2, ShieldCheck, Send } from 'lucide-react';
import { cn } from '../lib/utils';

// Nothing asked for, nothing sent, nothing verified.
const EMPTY_OTP = {
  sending: false, verifying: false, sent: false,
  code: '', token: '', mockCode: '', deliveryDetail: '', error: '',
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Stand-in grids for a branch with no hours configured; real quarter-hour seatings replace these once hours exist.
const LUNCH_SLOTS = ['12:00', '12:30', '13:00', '13:30', '14:00', '14:30'];
const DINNER_SLOTS = ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30'];

const toMin = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
const toHHMM = (m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;

const StepBadge = ({ n, done }) => (
  <span className={cn(
    'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold shrink-0',
    done ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500 border border-gray-200'
  )}>
    {done ? <CheckCircle className="h-3.5 w-3.5" /> : n}
  </span>
);

export default function ReservationPage({ restaurantId: propRestaurantId }) {
  const { restaurantId: ctxRestaurantId, locationId } = useRestaurant();
  const restaurantId = propRestaurantId || ctxRestaurantId || 1;
  const { toast } = useToast();

  // Selection state
  const [selectedDate, setSelectedDate] = useState(() => toDateStr(new Date()));
  const [selectedTime, setSelectedTime] = useState(null);
  const [showDinner, setShowDinner] = useState(false);
  const [duration] = useState(120);
  const [guestCount, setGuestCount] = useState(2);

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState('ALL');
  const [selectedRoom, setSelectedRoom] = useState('ALL');

  // Booking form (details modal)
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerTitle, setCustomerTitle] = useState('');
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [bookingMode, setBookingMode] = useState('EXCLUSIVE');
  const [loyaltyId, setLoyaltyId] = useState('');
  const [tags, setTags] = useState('');
  const [bookingSource, setBookingSource] = useState('WALK_IN');
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [scheduleReminder, setScheduleReminder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /* Arriving from a guest's CRM profile ("New reservation"): the phone and
     name come in the URL, so the desk does not retype what the profile
     already knows. Read once; the page holds no router state of its own. */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const phone = q.get('phone');
    if (!phone) return;
    setCustomerPhone(String(phone).replace(/\D/g, '').slice(-8));
    if (q.get('name')) setCustomerName(q.get('name'));
    setShowBookingForm(true);
  }, []);

  // What the party is coming for: the regular menu or a buffet, mirroring the website's and the till's forms.
  const [dining, setDining] = useState('ALACARTE');
  const [buffetId, setBuffetId] = useState('');
  /* How many of the party are children, asked only for a buffet, the only booking that prices and preps them apart. */
  const [kidCount, setKidCount] = useState(0);
  const [buffets, setBuffets] = useState([]);

  // Phone → CRM customer lookup
  const [phoneLookupLoading, setPhoneLookupLoading] = useState(false);
  const [matchedCustomer, setMatchedCustomer] = useState(null);

  /* Phone verification per the branch's offline policy; a null policy reads as "not required", since the server decides. */
  const [policy, setPolicy] = useState(null);
  // The branch's cap on one booking's party; no cap set = the old ceiling.
  const maxParty = Number(policy?.max_party_size) || 50;
  const [otp, setOtp] = useState(EMPTY_OTP);

  /* Which rule applies: the Source dropdown offers more labels than the server stores, and non-PHONE files as walk-in. */
  const otpChannel = bookingSource === 'PHONE' ? 'PHONE' : 'RECEPTION';
  const otpNeeded = !!(policy && (otpChannel === 'PHONE'
    ? policy.phone_booking_otp_required
    : policy.walkin_booking_otp_required));
  const otpDone = !!otp.token;

  useEffect(() => {
    let alive = true;
    getReservationSettings(restaurantId, locationId)
      .then((s) => { if (alive) setPolicy(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, [restaurantId, locationId]);

  /* The branch's weekly hours and cut-offs - the same bounds the website builds its Time list from. */
  const [branchHours, setBranchHours] = useState(null);
  useEffect(() => {
    if (!restaurantId || !locationId) return;
    let alive = true;
    locationsService.get(restaurantId, locationId)
      .then((loc) => {
        if (!alive) return;
        setBranchHours({
          hours: Array.isArray(loc?.operating_hours) && loc.operating_hours.length === 7
            ? loc.operating_hours : null,
          lastOrders: Array.isArray(loc?.last_order_times) ? loc.last_order_times : null,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [restaurantId, locationId]);

  /* The selected day's seatings, every quarter hour from opening to last orders; null means no hours configured. */
  const daySlots = useMemo(() => {
    if (!branchHours?.hours) return null;
    const idx = (new Date(`${selectedDate}T00:00:00`).getDay() + 6) % 7; // Mon=0
    const d = branchHours.hours[idx];
    if (!d || d.closed || !d.open || !d.close) return { closed: true, slots: [] };
    const open = toMin(d.open);
    const close = toMin(d.close);
    const lastRaw = branchHours.lastOrders ? branchHours.lastOrders[idx] : null;
    let until = Math.min(close ?? -1, toMin(lastRaw) ?? close ?? -1);
    let from = Math.ceil(open / 15) * 15;
    /* A BUFFET is seated for its OWN service, which is the narrower fact: the
       branch may be open till eleven, but a counter that finishes at five
       cannot take a party at half five. The last seating is a quarter hour
       BEFORE it ends — a party sat down as the counter closes is served
       nothing. A buffet that never stated its hours narrows nothing. */
    const bw = (dining === 'BUFFET' && buffetId)
      ? buffets.find((b) => String(b.id) === String(buffetId)) : null;
    if (bw?.start_time && bw?.end_time && toMin(bw.end_time) > toMin(bw.start_time)) {
      from = Math.max(from, Math.ceil(toMin(bw.start_time) / 15) * 15);
      until = Math.min(until, toMin(bw.end_time) - 15);
    }
    if (open == null || until < from) return { closed: true, slots: [], buffet: bw || null };
    /* A buffet sold in SITTINGS offers ITS start times and nothing else —
       10:00, 10:15, 10:30 — because the server refuses a booking that is not
       on one. Offering every quarter hour would let the desk type 10:07 and
       be told no at the end of the form, so the strip stops offering it.
       A sitting with no seats left is left out for the same reason. */
    if (bw && Array.isArray(bw.sittings) && bw.sittings.length) {
      const starts = bw.sittings
        .filter((s) => s.seats_left == null || s.seats_left > 0)
        .map((s) => s.start)
        .filter((tm) => toMin(tm) >= from && toMin(tm) <= until);
      return { closed: starts.length === 0, slots: starts, open: d.open,
        close: d.close, last: lastRaw, buffet: bw };
    }
    const slots = [];
    for (let m = from; m <= until; m += 15) slots.push(toHHMM(m));
    return { closed: false, slots, open: d.open, close: d.close, last: lastRaw, buffet: bw || null };
  }, [branchHours, selectedDate, dining, buffetId, buffets]);

  /* Picking a buffet after the time narrows the day under it: hold the choice
     when it survives, else snap to the nearest seating still offered rather
     than leaving a time on screen the counter cannot serve. */
  useEffect(() => {
    if (!selectedTime || !daySlots || daySlots.closed || !daySlots.slots.length) return;
    if (daySlots.slots.includes(selectedTime)) return;
    const want = toMin(selectedTime);
    setSelectedTime(daySlots.slots.reduce((best, s) => (
      Math.abs(toMin(s) - want) < Math.abs(toMin(best) - want) ? s : best), daySlots.slots[0]));
  }, [daySlots, selectedTime]);

  const lunchSlots = daySlots ? daySlots.slots.filter((t) => toMin(t) < 17 * 60) : LUNCH_SLOTS;
  const dinnerSlots = daySlots ? daySlots.slots.filter((t) => toMin(t) >= 17 * 60) : DINNER_SLOTS;
  // Show whichever half of the day has seatings, and only offer the lunch/dinner toggle when both exist.
  const showingDinner = dinnerSlots.length > 0 && (showDinner || lunchSlots.length === 0);
  const activeSlots = showingDinner ? dinnerSlots : lunchSlots;
  const canToggleMeal = lunchSlots.length > 0 && dinnerSlots.length > 0;

  // A code proves ONE number on ONE booking type, so changing either throws the proof away rather than carrying it.
  useEffect(() => { setOtp(EMPTY_OTP); }, [customerPhone, bookingSource]);

  /* Buffets on offer at this branch priced at the booked moment - the same public list every other surface reads. */
  useEffect(() => {
    if (!showBookingForm || !locationId) { setBuffets([]); return undefined; }
    let alive = true;
    /* Always dated, even before a time is picked: the list is for the day
       being booked, and a bare call would price it — and list its sittings —
       against today instead. Midday stands in for the hour until one is
       chosen, and the moment one is, the real time is sent. */
    const at = `${selectedDate}T${selectedTime || '12:00'}:00`;
    apiClient.get(`/customers/locations/${locationId}/buffets/`, { params: { at } })
      .then(({ data }) => {
        if (!alive) return;
        const rows = data?.data || [];
        setBuffets(rows);
        // Keep the picked buffet only while it still exists on the list.
        setBuffetId((id) => (id && rows.some((b) => String(b.id) === String(id)) ? id : ''));
      })
      .catch(() => { if (alive) setBuffets([]); });
    return () => { alive = false; };
  }, [showBookingForm, locationId, selectedDate, selectedTime]);

  const sendOtp = async () => {
    if (!customerPhone.trim()) {
      toast({ title: 'Phone needed', description: 'Enter the guest’s phone number first.', variant: 'destructive' });
      return;
    }
    setOtp((o) => ({ ...o, sending: true, error: '' }));
    try {
      const res = await requestReservationOtp(restaurantId, customerPhone.trim(), otpChannel);
      // `mock_code` comes back only when the code could not be texted; the
      // detail beside it says why, and both are absent on a real delivery.
      setOtp((o) => ({
        ...o, sending: false, sent: true, code: '', token: '',
        mockCode: res.mock_code || '', deliveryDetail: res.delivery_detail || '',
      }));
    } catch (e) {
      setOtp((o) => ({ ...o, sending: false, error: e?.error || e?.message || 'Could not send the code' }));
    }
  };

  const checkOtp = async () => {
    setOtp((o) => ({ ...o, verifying: true, error: '' }));
    try {
      const token = await verifyReservationOtp(
        restaurantId, customerPhone.trim(), otp.code, otpChannel);
      setOtp((o) => ({ ...o, verifying: false, token, mockCode: '', error: '' }));
      toast({ title: 'Phone verified', description: 'The number on this booking is confirmed.' });
    } catch (e) {
      setOtp((o) => ({ ...o, verifying: false, token: '', error: e?.error || e?.message || 'That code is not valid' }));
    }
  };

  useEffect(() => {
    const digits = (customerPhone || '').replace(/\D/g, '');
    if (!showBookingForm || digits.length < 8) {
      setMatchedCustomer(null);
      setPhoneLookupLoading(false);
      return;
    }
    let cancelled = false;
    setPhoneLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const profile = await crmService.profileByPhone(customerPhone.trim());
        if (cancelled) return;
        setMatchedCustomer(profile);
        const c = profile?.customer;
        if (c?.full_name) setCustomerName(prev => prev.trim() ? prev : c.full_name);
      } catch (err) {
        if (!cancelled) setMatchedCustomer(null); // no match — treated as a new customer
      } finally {
        if (!cancelled) setPhoneLookupLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [customerPhone, showBookingForm]);

  // ---- Date strip: next 14 days ----
  const dateChips = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      days.push({
        dateStr: toDateStr(d),
        dayNum: d.getDate(),
        month: d.toLocaleDateString(undefined, { month: 'short' }),
        weekday: i === 0 ? 'Today' : i === 1 ? 'Tmrw' : d.toLocaleDateString(undefined, { weekday: 'short' }),
      });
    }
    return days;
  }, []);

  const isSlotPast = useCallback((time) => {
    return new Date(`${selectedDate}T${time}:00`) < new Date();
  }, [selectedDate]);

  const isPastSelection = useCallback(() => {
    if (!selectedTime) return false;
    return new Date(`${selectedDate}T${selectedTime}:00`) < new Date();
  }, [selectedDate, selectedTime]);

  // Clear a selected time that fell into the past when the date changes
  useEffect(() => {
    if (selectedTime && isSlotPast(selectedTime)) setSelectedTime(null);
  }, [selectedDate, selectedTime, isSlotPast]);

  // ---- Load tables whenever date/time/guests change ----
  const loadTables = useCallback(async () => {
    if (!selectedTime) { setTables([]); return; }
    setLoading(true);
    try {
      const dateTime = `${selectedDate}T${selectedTime}:00`;
      const data = await getAllTables(restaurantId, {
        isActive: true,
        date: dateTime,
        guests: guestCount || 1,
      });
      setTables(data.tables || []);
    } catch (error) {
      console.error('Error loading tables:', error);
      toast({ title: 'Error', description: 'Failed to load tables', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedTime, guestCount, restaurantId, toast]);

  useEffect(() => { loadTables(); }, [loadTables]);

  const uniqueFloors = useMemo(() => {
    const map = new Map();
    tables.forEach(t => {
      if (t.room?.floor) map.set(t.room.floor.id, t.room.floor.name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [tables]);

  const uniqueRooms = useMemo(() => {
    const map = new Map();
    tables.forEach(t => {
      if (selectedFloor !== 'ALL' && t.room?.floor && String(t.room.floor.id) !== String(selectedFloor)) return;
      if (t.room) map.set(t.room.id, t.room.name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [tables, selectedFloor]);

  const filteredTables = useMemo(() => {
    return tables.filter(t => {
      if (selectedFloor !== 'ALL' && t.room?.floor && String(t.room.floor.id) !== String(selectedFloor)) return false;
      if (selectedRoom !== 'ALL' && t.room && String(t.room.id) !== String(selectedRoom)) return false;
      return true;
    });
  }, [tables, selectedFloor, selectedRoom]);

  function isTableBookable(table) {
    return table.status === 'AVAILABLE' || table.status === 'PARTIAL';
  }

  // Auto-assigned table: smallest bookable table in the chosen area that fits the party
  const bestFitTable = useMemo(() => {
    const fits = filteredTables
      .filter(t => isTableBookable(t) && t.capacity >= (guestCount || 1))
      .sort((a, b) => a.capacity - b.capacity);
    return fits[0] || null;
  }, [filteredTables, guestCount]);

  const readyToBook = selectedTime && !isPastSelection() && bestFitTable;

  const ctaLabel = () => {
    if (!selectedTime) return 'Select a date and time';
    if (isPastSelection()) return 'That time has passed — pick another';
    const t = bestFitTable;
    if (!t) return loading ? 'Checking availability…' : 'No free table — try another time';
    const d = new Date(`${selectedDate}T00:00:00`);
    const dLabel = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    return `Reserve ${t.tableCode} · ${dLabel} · ${selectedTime}`;
  };

  const openBookingForm = () => {
    if (!readyToBook) return;
    setShowBookingForm(true);
  };

  const closeBookingForm = () => {
    setShowBookingForm(false);
    setCustomerName('');
    setCustomerTitle('');
    setCustomerPhone('');
    setAdvanceAmount('');
    setDining('ALACARTE');
    setBuffetId('');
  };

  const handleBookTable = async () => {
    const table = bestFitTable;
    if (!table) return;

    if (isPastSelection()) {
      toast({
        title: 'Invalid Date/Time',
        description: 'This reservation slot is now in the past. Please pick a future date and time.',
        variant: 'destructive',
      });
      return;
    }
    if (!customerName.trim()) {
      toast({ title: 'Validation Error', description: 'Please enter customer name', variant: 'destructive' });
      return;
    }
    if (otpNeeded && !otpDone) {
      toast({
        title: 'Phone not verified',
        description: 'This branch needs the guest’s number verified before the table is held.',
        variant: 'destructive',
      });
      return;
    }
    if (dining === 'BUFFET' && !buffetId) {
      toast({
        title: 'Which buffet?',
        description: 'Pick the buffet the party is booking, or switch back to à la carte.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const startDateTime = `${selectedDate}T${selectedTime}:00`;
      const endDateTime = new Date(new Date(startDateTime).getTime() + duration * 60000).toISOString();

      const bookingData = {
        booking_type: 'appointment',
        mode: bookingMode,
        pax: guestCount || 1,
        customer_name: customerName,
        customer_title: customerTitle || null,
        deposit_amount: advanceAmount ? parseFloat(advanceAmount) : 0,
        customer_phone: customerPhone || null,
        reserved_start_at: startDateTime,
        reserved_end_at: endDateTime,
        notes: null,
        booking_source: bookingSource,
        // A la carte or which buffet, in the same fields the website and till send; the server checks the branch owns it.
        dining_type: dining,
        ...(dining === 'BUFFET' ? { buffet_id: Number(buffetId) } : {}),
        // Clamped on the way out as well as in the box, since `pax` can be wound down after the children are set.
        kids: dining === 'BUFFET' ? Math.min(kidCount, guestCount || 1) : 0,
        loyalty_id: loyaltyId || null,
        customer_tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        high_chair_needed: false,
        occasion: 'CASUAL',
        confirmation_sent: sendConfirmation,
        reminder_scheduled: scheduleReminder,
        // Only present when a code was taken; the server ignores it otherwise.
        ...(otp.token ? { otp_token: otp.token } : {}),
      };

      await bookTable(restaurantId, table.id, bookingData);

      toast({
        title: 'Booking Successful',
        description: `Table ${table.tableCode} has been reserved for ${customerName}`,
      });

      closeBookingForm();
      setSelectedTime(null);
      setLoyaltyId('');
      setTags('');
      setBookingSource('WALK_IN');
      setBookingMode('EXCLUSIVE');
      setSendConfirmation(false);
      setScheduleReminder(false);
      loadTables();
    } catch (error) {
      console.error('Error booking table:', error);
      toast({
        title: 'Booking Failed',
        description: error.error || error.message || 'Failed to book table',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const slotButton = (time) => {
    const past = isSlotPast(time);
    const selected = selectedTime === time;
    return (
      <button
        key={time}
        disabled={past}
        onClick={() => setSelectedTime(time)}
        className={cn(
          'py-2.5 rounded-lg border text-sm font-semibold transition-colors',
          selected
            ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
            : past
              ? 'border-gray-200 text-gray-300 line-through cursor-not-allowed bg-gray-50'
              : 'border-gray-200 text-gray-700 bg-white hover:border-orange-400 hover:text-orange-600'
        )}
      >
        {time}
      </button>
    );
  };

  const chosenTable = bestFitTable;

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="w-full p-4 lg:px-8 space-y-4">
        {/* One job: take a booking. Requests awaiting a decision are answered on All Reservations. */}
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Create new reservation</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
        {/* 1 · Date */}
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="py-3 px-4 border-b border-gray-100">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <StepBadge n={1} done={!!selectedDate} /> Date
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {dateChips.map(d => {
                const selected = selectedDate === d.dateStr;
                return (
                  <button
                    key={d.dateStr}
                    onClick={() => setSelectedDate(d.dateStr)}
                    className={cn(
                      'flex flex-col items-center justify-center shrink-0 w-[72px] py-2.5 rounded-xl border transition-colors',
                      selected
                        ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-orange-400'
                    )}
                  >
                    <span className={cn('text-[11px] font-medium', selected ? 'text-orange-100' : 'text-gray-500')}>{d.weekday}</span>
                    <span className="text-xl font-bold leading-tight">{d.dayNum}</span>
                    <span className={cn('text-[11px]', selected ? 'text-orange-100' : 'text-gray-500')}>{d.month}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* 2 · Time — seatings from the branch's real hours, like the website */}
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="py-3 px-4 border-b border-gray-100 flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <StepBadge n={2} done={!!selectedTime} /> Time
            </CardTitle>
            <span className="text-xs text-gray-400">
              {/* A buffet booking is seated for the buffet's hours, so that is what the hint has to say. */}
              {daySlots && !daySlots.closed
                ? (daySlots.buffet
                  ? `${daySlots.buffet.name} served ${daySlots.buffet.start_time}–${daySlots.buffet.end_time}`
                  : `Open ${daySlots.open}–${daySlots.close}${daySlots.last ? ` · last orders ${daySlots.last}` : ''}`)
                : daySlots?.closed
                  ? 'Closed this day'
                  : showingDinner ? 'Dinner 18:00–23:00' : 'Lunch 12:00–15:00'}
            </span>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {daySlots?.closed ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 text-center">
                {/* A buffet with nothing left says so in ITS hours: the branch may well still be seating. */}
                {daySlots.buffet
                  ? `${daySlots.buffet.name} is served ${daySlots.buffet.start_time}–${daySlots.buffet.end_time} — nothing left to book on this date.`
                  : 'The branch is closed on this day — pick another date.'}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 xl:grid-cols-5 gap-2">
                  {activeSlots.map(slotButton)}
                </div>
                {canToggleMeal && (
                  <button
                    onClick={() => setShowDinner(v => !v)}
                    className="w-full py-2.5 rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-orange-400 hover:text-orange-600 transition-colors"
                  >
                    {showingDinner ? 'Show lunch times' : 'Show dinner times'}
                  </button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* 3 · Guests */}
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="py-3 px-4 border-b border-gray-100">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <StepBadge n={3} done /> Guests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <button
                /* Winding the party down winds the children down too, or the form reads "2 guests, 4 children" until the server objects. */
                onClick={() => {
                  const n = Math.max(1, guestCount - 1);
                  setGuestCount(n);
                  setKidCount(k => Math.min(k, n));
                }}
                className="h-12 w-12 rounded-full border border-gray-300 text-2xl text-gray-700 hover:bg-gray-50"
              >−</button>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">{guestCount}</div>
                <div className="text-xs text-gray-500">guests</div>
              </div>
              <button
                onClick={() => setGuestCount(c => Math.min(maxParty, c + 1))}
                disabled={guestCount >= maxParty}
                className="h-12 w-12 rounded-full border border-gray-300 text-2xl text-gray-700 hover:bg-gray-50"
              >+</button>
            </div>

            {/* The split is asked where the party size is, and only for a buffet, which charges and preps children apart. */}
            {dining === 'BUFFET' && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                <div>
                  <div className="text-sm font-medium text-gray-800">of whom children</div>
                  <div className="text-xs text-gray-500">
                    {kidCount === 0
                      ? 'priced and prepped as adults'
                      : `${Math.max(0, guestCount - kidCount)} adult${guestCount - kidCount === 1 ? '' : 's'} · ${kidCount} child${kidCount === 1 ? '' : 'ren'}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setKidCount(k => Math.max(0, k - 1))}
                    disabled={kidCount === 0}
                    className="h-9 w-9 rounded-full border border-gray-300 text-xl text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    aria-label="One child fewer"
                  >−</button>
                  <span className="w-6 text-center text-lg font-semibold tabular-nums text-gray-900">
                    {kidCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setKidCount(k => Math.min(guestCount, k + 1))}
                    disabled={kidCount >= guestCount}
                    className="h-9 w-9 rounded-full border border-gray-300 text-xl text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    aria-label="One child more"
                  >+</button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        <div className="space-y-4">
        {/* 4 · Table (optional) */}
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="py-3 px-4 border-b border-gray-100 flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <StepBadge n={4} done={selectedFloor !== 'ALL' || selectedRoom !== 'ALL'} /> Seating area
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!selectedTime ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500 text-center">
                Pick a date and time first to see which areas are free.
              </div>
            ) : loading ? (
              <div className="py-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking availability…
              </div>
            ) : (
              <>
                {/* Floor / area tabs */}
                {(uniqueFloors.length > 1 || uniqueRooms.length > 1) && (
                  <div className="space-y-2">
                    {uniqueFloors.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto">
                        <button
                          onClick={() => { setSelectedFloor('ALL'); setSelectedRoom('ALL'); }}
                          className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0',
                            selectedFloor === 'ALL' ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400')}
                        >All floors</button>
                        {uniqueFloors.map(f => (
                          <button
                            key={f.id}
                            onClick={() => { setSelectedFloor(String(f.id)); setSelectedRoom('ALL'); }}
                            className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0',
                              String(selectedFloor) === String(f.id) ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400')}
                          >{f.name}</button>
                        ))}
                      </div>
                    )}
                    {uniqueRooms.length > 1 && (
                      <div className="grid grid-cols-3 gap-2">
                        {[{ id: 'ALL', name: 'All areas' }, ...uniqueRooms].slice(0, 6).map(r => (
                          <button
                            key={r.id}
                            onClick={() => setSelectedRoom(String(r.id))}
                            className={cn('py-2 rounded-lg border text-sm font-medium truncate px-2',
                              String(selectedRoom) === String(r.id)
                                ? 'bg-orange-50 border-orange-400 text-orange-700'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300')}
                          >{r.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Auto-assignment summary — the best free table in the chosen area */}
                {bestFitTable ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                    <div className="text-sm text-emerald-900">
                      <span className="font-semibold">A table is available here.</span>{' '}
                      We'll assign the best free table for {guestCount} {guestCount === 1 ? 'guest' : 'guests'} automatically.
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500 text-center">
                    No free table in this area for that time and party size. Try another area or time.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        </div>
        </div>

        {/* A plain in-flow confirm bar, NOT sticky: the shell's pb-28 safe area would park a sticky bar over the Guests card. */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
          <Button
            onClick={openBookingForm}
            disabled={!readyToBook}
            className={cn(
              'w-full h-12 rounded-xl text-base font-semibold',
              readyToBook ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed'
            )}
          >
            {ctaLabel()}
          </Button>
        </div>
      </div>

      {/* Guest details modal */}
      {showBookingForm && chosenTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl">
            <CardHeader className="border-b border-gray-100 py-4">
              <CardTitle className="text-lg">Who's coming?</CardTitle>
              <p className="text-sm text-gray-500 font-normal">
                Table <strong>{chosenTable.tableCode}</strong> · {selectedDate} · {selectedTime} · {guestCount} guests · {duration} min
              </p>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {/* Which kind of booking this is, asked first, since phone and walk-in carry different rules and sources. */}
              <div>
                <Label>Booking type</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    ['WALK_IN', 'Walk-in', 'Guest at the counter, booking ahead'],
                    ['PHONE', 'Phone', 'Guest rang the restaurant'],
                  ].map(([v, l, hint]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setBookingSource(v)}
                      className={cn(
                        'rounded-lg border p-3 text-left transition-colors',
                        bookingSource === v
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 bg-white hover:border-orange-300'
                      )}
                    >
                      <div className={cn('text-sm font-semibold', bookingSource === v ? 'text-orange-700' : 'text-gray-800')}>
                        {l}
                      </div>
                      <div className="text-[11px] text-gray-500">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* A la carte or one of the branch's buffets, priced at the booked moment; hidden when the branch runs none. */}
              {buffets.length > 0 && (
                <div>
                  <Label>Dining</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      ['ALACARTE', 'À la carte', 'Ordering from the menu'],
                      ['BUFFET', 'Open buffet', 'Per-head buffet seating'],
                    ].map(([v, l, hint]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          setDining(v);
                          // One buffet on offer — picking the mode picks it.
                          if (v === 'BUFFET' && buffets.length === 1) setBuffetId(String(buffets[0].id));
                        }}
                        className={cn(
                          'rounded-lg border p-3 text-left transition-colors',
                          dining === v
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 bg-white hover:border-orange-300'
                        )}
                      >
                        <div className={cn('text-sm font-semibold', dining === v ? 'text-orange-700' : 'text-gray-800')}>
                          {l}
                        </div>
                        <div className="text-[11px] text-gray-500">{hint}</div>
                      </button>
                    ))}
                  </div>
                  {dining === 'BUFFET' && (
                    <select
                      value={buffetId}
                      onChange={(e) => setBuffetId(e.target.value)}
                      className="mt-2 w-full h-10 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                    >
                      <option value="">Pick a buffet…</option>
                      {buffets.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} — {b.adult_price}/head
                          {b.start_time ? ` · ${b.start_time}–${b.end_time || ''}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Customer name with title */}
              <div>
                <Label htmlFor="customerName">Customer name *</Label>
                <div className="flex gap-2 mt-1">
                  <select
                    className="h-10 w-24 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm focus:outline-none focus:border-orange-400"
                    value={customerTitle}
                    onChange={(e) => setCustomerTitle(e.target.value)}
                  >
                    <option value="">Title</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter customer name"
                    className="flex-1"
                    required
                  />
                </div>
              </div>

              {/* Phone with CRM lookup */}
              <div>
                <Label htmlFor="customerPhone">Phone number</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="mt-1"
                />
                {phoneLookupLoading && (
                  <p className="text-xs text-gray-500 mt-1">Looking up customer…</p>
                )}
                {!phoneLookupLoading && matchedCustomer?.customer && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900 space-y-0.5">
                    <p className="font-semibold flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" /> {matchedCustomer.customer.full_name || 'Existing customer'}
                    </p>
                    {matchedCustomer.customer.email && <p>{matchedCustomer.customer.email}</p>}
                    {matchedCustomer.stats && (
                      <p className="text-green-800">
                        {matchedCustomer.stats.visit_count || 0} visits
                        {matchedCustomer.stats.total_spend ? ` · spend ${Number(matchedCustomer.stats.total_spend).toFixed(2)}` : ''}
                        {matchedCustomer.stats.last_visit ? ` · last visit ${String(matchedCustomer.stats.last_visit).slice(0, 10)}` : ''}
                      </p>
                    )}
                    <button
                      type="button"
                      className="text-xs text-green-700 underline"
                      onClick={() => setCustomerName(matchedCustomer.customer.full_name || '')}
                    >
                      Use this name
                    </button>
                  </div>
                )}

                {/* Verifying the number when this branch asks for it, sitting with the phone field, since that is what it is about. */}
                {otpNeeded && (
                  <div className={cn(
                    'mt-2 rounded-lg border p-3 text-sm space-y-2',
                    otpDone
                      ? 'border-green-200 bg-green-50 text-green-900'
                      : 'border-amber-200 bg-amber-50 text-amber-900'
                  )}>
                    <p className="font-semibold flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4" />
                      {otpDone
                        ? 'Phone number verified'
                        : `${otpChannel === 'PHONE' ? 'Phone' : 'Walk-in'} bookings need the number verified`}
                    </p>
                    {!otpDone && (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button" variant="outline" size="sm"
                          disabled={otp.sending || !customerPhone.trim()}
                          onClick={sendOtp}
                        >
                          {otp.sending
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Send className="h-3.5 w-3.5" />}
                          <span className="ml-1.5">{otp.sent ? 'Send again' : 'Send code'}</span>
                        </Button>
                        {otp.sent && (
                          <>
                            <Input
                              className="h-9 w-28 text-center tracking-[0.3em] font-bold"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="——————"
                              value={otp.code}
                              onChange={(e) => setOtp((o) => ({
                                ...o, code: e.target.value.replace(/\D/g, '').slice(0, 6), error: '',
                              }))}
                              aria-label="Verification code"
                            />
                            <Button
                              type="button" size="sm"
                              disabled={otp.verifying || otp.code.length < 4}
                              onClick={checkOtp}
                            >
                              {otp.verifying
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <CheckCircle className="h-3.5 w-3.5" />}
                              <span className="ml-1.5">Verify</span>
                            </Button>
                            {/* Only present when the code was NOT texted — either no
                                gateway is set up under Settings → Notifications → SMS,
                                or the gateway refused it. `delivery_detail` says which,
                                so staff aren't left guessing whether the guest has it. */}
                            {otp.mockCode && (
                              <span
                                className="rounded bg-amber-100 px-2 py-1 text-xs font-semibold"
                                title={otp.deliveryDetail || ''}
                              >
                                Read out: {otp.mockCode} (not texted)
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {otp.error && <p className="text-xs font-medium text-red-600">{otp.error}</p>}
                  </div>
                )}
              </div>

              {/* Mode, advance & loyalty */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bookingMode">Booking mode</Label>
                  <select
                    id="bookingMode"
                    value={bookingMode}
                    onChange={(e) => setBookingMode(e.target.value)}
                    className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                  >
                    <option value="EXCLUSIVE">Exclusive</option>
                    <option value="SHARE">Share</option>
                    <option value="JOIN">Join</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="advanceAmount">Advance amount</Label>
                  <Input
                    id="advanceAmount"
                    type="number"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="loyaltyId">Loyalty ID</Label>
                  <Input
                    id="loyaltyId"
                    value={loyaltyId}
                    onChange={(e) => setLoyaltyId(e.target.value)}
                    placeholder="Member ID"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="VIP, Allergy, Regular..."
                  className="mt-1"
                />
              </div>

              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendConfirmation}
                    onChange={(e) => setSendConfirmation(e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                  />
                  Send confirmation
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleReminder}
                    onChange={(e) => setScheduleReminder(e.target.checked)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                  />
                  Schedule reminder
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-3">
                <Button variant="outline" onClick={closeBookingForm} className="flex-1 rounded-lg">
                  Back
                </Button>
                {/* Greyed rather than hidden while the number is unverified: a button that disappears leaves staff hunting. */}
                <Button
                  onClick={handleBookTable}
                  disabled={submitting || (otpNeeded && !otpDone)}
                  title={otpNeeded && !otpDone ? 'Verify the guest’s phone number first' : undefined}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold"
                >
                  {submitting ? 'Booking…' : 'Confirm booking'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
